import { useCallback, useMemo } from 'react'
import {
  MODALSTATE,
  ModalAtom,
  DialerStateDefaultValue,
  CallDurationLabelAtom,
  SelectedResidentContactAtom,
  CallContactAtom,
  TwilioActiveDevice,
  TwilioActiveCallConnection,
} from './contact-center.store'
import { useAtom } from 'jotai'
import React from 'react'
import { useDialerState } from './dialer/dialer.hooks'
import { useQuery } from 'react-query'
import { useClient } from 'utils/auth-context'

export const enum MICROPHONE_PERMISSION_STATUS {
  GRANTED,
  DENIED,
  BLOCKED,
  PROMPT,
  NONE,
}

export const MICROPHONE_PERMISSION_STATUS_MESSAGES = {
  [MICROPHONE_PERMISSION_STATUS.GRANTED]: 'Microphone Permissions Granted.',
  [MICROPHONE_PERMISSION_STATUS.DENIED]: 'Microphone Permissions Denied.',
  [MICROPHONE_PERMISSION_STATUS.BLOCKED]: 'Microphone Permissions Blocked.',
  [MICROPHONE_PERMISSION_STATUS.PROMPT]: 'Microphone Permissions Required.',
}

interface IMicrophonePermissions {
  setErrorMessage: (message: MICROPHONE_PERMISSION_STATUS) => void
}
export const useMicrophonePermissions = ({ setErrorMessage }: IMicrophonePermissions) => {
  async function launchBrowserMicrophoneAllowPrompt() {
    try {
      const res = await navigator.mediaDevices.getUserMedia({ audio: true, video: false })

      return res
    } catch (err) {
      setErrorMessage(MICROPHONE_PERMISSION_STATUS.DENIED)

      return false
    }
  }
  async function queryBrowserMicrophonePermission() {
    const result = await navigator.permissions.query({ name: 'microphone' as PermissionName })

    if (result.state === 'granted') {
      setErrorMessage(MICROPHONE_PERMISSION_STATUS.NONE)
    } else if (result.state === 'prompt') {
      setErrorMessage(MICROPHONE_PERMISSION_STATUS.PROMPT)
    } else if (result.state === 'denied') {
      setErrorMessage(MICROPHONE_PERMISSION_STATUS.DENIED)
    }

    result.onchange = ev => {
      if ((ev.currentTarget as PermissionStatus).state === 'prompt')
        setErrorMessage(MICROPHONE_PERMISSION_STATUS.PROMPT)

      if ((ev.currentTarget as PermissionStatus).state === 'granted') {
        setErrorMessage(MICROPHONE_PERMISSION_STATUS.NONE)
      }

      if ((ev.currentTarget as PermissionStatus).state === 'denied') {
        setErrorMessage(MICROPHONE_PERMISSION_STATUS.DENIED)
      }
    }

    return result
  }

  React.useEffect(() => {
    //queryBrowserMicrophonePermission()
  }, [])

  return {
    launchPrompt: launchBrowserMicrophoneAllowPrompt,
    queryPermissions: queryBrowserMicrophonePermission,
  }
}

export const useContactCenterModalEvents = () => {
  const [modalState, setModalState] = useAtom(ModalAtom)
  const { setDialerState } = useDialerState()
  const { setSelected: setSelectedContact } = useSelectedResidentContact()
  const { setLabel } = useCallDurationLabel()

  const onHide = useCallback(() => {}, [])

  const onClose = useCallback(() => {
    setDialerState(DialerStateDefaultValue)
    setModalState(MODALSTATE.CLOSE)
    setLabel('')
    setSelectedContact(undefined)
  }, [])

  const isOpen = useMemo(() => {
    return modalState === MODALSTATE.SHOW
  }, [modalState])

  const open = useCallback(() => {
    setModalState(MODALSTATE.SHOW)
  }, [])

  const hide = useCallback(() => {
    setModalState(MODALSTATE.HIDE)
  }, [])

  const close = useCallback(() => {
    onClose()
    document.dispatchEvent(new Event('contact-center-close'))
  }, [])

  return {
    onHide: onHide,
    onClose: onClose,
    isOpen: isOpen,
    open: open,
    hide: hide,
    close: close,
  }
}

export const useAgentName = () => {
  const AGENT_LABEL = 'Devtek admin'
  return AGENT_LABEL
}

export const useAgentPhoneNo = () => {
  const PHONE_NO = process.env.REACT_APP_TWILIO_CALLING_FROM_NUMBER
  return PHONE_NO
}

export const useAgentInfo = () => {
  const name = useAgentName()
  const phoneNo = useAgentPhoneNo()
  return {
    name,
    phoneNo,
  }
}

export const useCallDurationLabel = () => {
  const [label, setLabel] = useAtom(CallDurationLabelAtom)
  return {
    label,
    setLabel,
  }
}

export const useSelectedResidentContact = () => {
  const [selectedResident, setSelectedResident] = useAtom(SelectedResidentContactAtom)
  return {
    selected: selectedResident,
    setSelected: setSelectedResident,
  }
}

export const useContactCenterModalState = () => {
  const [modalState, setModalState] = useAtom(ModalAtom)
  return {
    modalState,
    setModalState,
  }
}

export const useCallContact = () => {
  const [callFunc, setCallFunc] = useAtom(CallContactAtom)
  return {
    callFunc,
    setCallFunc,
  }
}

export const useTwilioDevice = () => {
  const [device, setDevice] = useAtom(TwilioActiveDevice)
  return {
    device,
    setDevice,
  }
}

export const useTwilioCallConnection = () => {
  const [connection, setConnection] = useAtom(TwilioActiveCallConnection)
  return {
    connection,
    setConnection,
  }
}

export const useAllResidents = () => {
  const client = useClient()
  const { data, isLoading } = useQuery(['all_residents'], async () => {
    const response = await client('properties/residents/', {})
    return response?.data
  })

  return {
    data,
    isLoading,
  }
}
